/* Copyright (c) 2017 Backpack Health LLC */

import * as _ from 'lodash';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

import { TextLink } from '../../../../components/TextLink';
import { Button } from '../../../../components/buttons/Buttons';
import DropdownList from '../../../../components/DropdownList';
import IconClose from '../../../../components/icons/IconClose';

import DateUtil from '../../../../../utilities/DateUtil';
import FlexRow from '../../../../components/FlexRow';
import { DateTimeModel, iDateTime } from '../../../../../models/DateTimeModel';

const TIME_PICKER_PERIOD = {
  AM: 'AM',
  PM: 'PM',
};
interface PickerProps {
  phraseText: {
    label: any;
    submit: any;
  };

  onSubmit(): void;
  onClose(): void;
  onChange(i, f, v): void;
  fields: {
    value: any;
    options: any;
    label: any;
  }[];
  alternateActions?: any;
  style?: any;
}
const PickerModal: React.FC<PickerProps> = (props) => {
  const { phraseText, style, onClose, fields, onChange, onSubmit, alternateActions } = props;

  return (
    <div className="cmp-date-picker" style={style}>
      <div className="cmp-date-picker__content">
        <div className="font-caption-1 font-color-gsMiddle" style={{ display: 'inline-block' }}>
          <FormattedMessage {...phraseText.label} />
        </div>
        <div
          className="cmp-date-picker__close"
          onClick={onClose}
        >
          <IconClose.Small color={'red'} />
        </div>

        <div className="cmp-date-picker__inputs">
          {
            fields.map((f, i) => {
              return (
                <div className="cmp-date-picker__month" key={i} style={{ flex: '1 0 60px' }}>
                  <DropdownList
                    label={f.label}
                    value={f.value}
                    onChange={(event) => {
                      onChange(i, f, event.target.value);
                    }}
                    values={f.options}
                  />
                </div>
              );
            })
          }
        </div>
      </div>
      <div className="cmp-date-picker__finished __large-screen-only">
        <FlexRow>
          {
            alternateActions ? (
              alternateActions
            ) : null
          }
          <Button
            type="primary"
            style={{ minWidth: 110 }}
            onClick={(event) => {
              event.preventDefault();
              onSubmit();
            }}
          >
            <FormattedMessage {...phraseText.submit} />
          </Button>
        </FlexRow>
      </div>
      <div className="cmp-date-picker__finished __medium-screen-only">
        <FlexRow>
          {
            alternateActions ? (
              alternateActions
            ) : null
          }
        </FlexRow>
        <FlexRow style={{ justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            style={{ marginTop: 24, minWidth: 110 }}
            onClick={(event) => {
              event.preventDefault();
              onSubmit();
            }}
          >
            <FormattedMessage {...phraseText.submit} />
          </Button>
        </FlexRow>
      </div>
    </div>
  );
};


interface Props {
  phraseLabel: any;
  onDateSubmit(d): void;
  allowNull: boolean;
  allowToday: boolean;
  allowFuzzy: boolean;

  date: string;
  precision: string;
  earliestDateLimit: string;
  latestDateLimit: string;
  defaultDate: string;
  defaultPrecision: string;
}
export const DatePicker: React.FC<Props> = (props) => {
  const { phraseLabel, allowToday, allowNull, allowFuzzy, onDateSubmit } = props;

  const earliestDateLimit = props.earliestDateLimit || `${new Date().getFullYear() - 200}-01-01`;
  const latestDateLimit = props.latestDateLimit || new Date().toISOString().split('T')[0];

  const [dateState, setDateState] = React.useState(() => {
    const defaultPrecision = props.defaultPrecision || 'P1Y';
    const defaultDate = props.date ? props.date : (props.defaultDate || '');
    const precision = props.precision ? props.precision : defaultPrecision;
    const dateData = DateUtil.breakIntoParts(defaultDate, precision);

    return {
      month: dateData.month,
      day: dateData.day,
      year: dateData.year || new Date().getFullYear(),
      monthOptions: DateUtil.getMonthOptions(allowFuzzy),
      yearOptions: DateUtil.getYearOptions(earliestDateLimit, latestDateLimit),
      dayOptions: DateUtil.getDayOptions(dateData.year, dateData.month, allowFuzzy),
    };
  });

  const changeDate = (keyName, strValue) => {
    const numValue = Number(strValue);

    const newDateData = {
      year: dateState.year,
      month: dateState.month,
      day: dateState.day,
      [keyName]: _.isNaN(numValue) ? '' : `${numValue}`,
    };

    const dateData = DateUtil.insureValidDateData(newDateData.year, newDateData.month, newDateData.day, keyName, earliestDateLimit, latestDateLimit);
    const dayOptions = DateUtil.getDayOptions(dateData.year, dateData.month, allowFuzzy);

    setDateState({
      ...dateState,
      year: dateData.year,
      month: dateData.month,
      day: dateData.day,
      dayOptions,
    });
  };

  return (
    <PickerModal
      phraseText={{
        label: { id: phraseLabel },
        submit: { id: 'fuzzydate.submit.button.label' },
      }}
      onSubmit={() => {
        let internalDate = DateUtil.convertPartsToInternalFormat(dateState.year, dateState.month, dateState.day);
        const isFuzzy = !dateState.year || !dateState.month || !dateState.day;

        if (isFuzzy && !allowFuzzy) {
          internalDate = { date: '', precision: '' };
        }

        if (!allowNull && !internalDate.date) {
          return;
        }

        onDateSubmit({
          date: internalDate.date,
          precision: internalDate.precision,
        });
      }}
      onClose={() => {
        onDateSubmit(null);
      }}
      onChange={(i, f, v) => {
        const keys = ['month', 'day', 'year'];
        changeDate(keys[i], v);
      }}
      fields={[
        {
          value: dateState.month,
          options: dateState.monthOptions,
          label: <FormattedMessage id="fuzzydate.month.field.label" />,
        },
        {
          value: dateState.day,
          options: dateState.dayOptions,
          label: <FormattedMessage id="fuzzydate.day.field.label" />,
        },
        {
          value: dateState.year,
          options: dateState.yearOptions,
          label: <FormattedMessage id="fuzzydate.year.field.label" />,
        },
      ]}
      alternateActions={(
        <>
          {
            allowNull ? (
              <div style={{ minWidth: 60 }}>
                <TextLink
                  onClick={() => {
                    setDateState({
                      ...dateState,
                      month: '',
                      day: '',
                    });

                    onDateSubmit({
                      date: '',
                      precision: '',
                    });
                  }}
                >
                  <div className="font-caption-1">
                    <FormattedMessage id="fuzzydate.unsure.button.label" />
                  </div>
                </TextLink>
              </div>
            ) : null
          }
          {
            allowToday ? (
              <div style={{ minWidth: 60 }}>
                <TextLink
                  onClick={() => {
                    const todayParts = DateUtil.breakIntoParts(DateUtil.today(), 'P1D');

                    setDateState({
                      ...dateState,
                      year: todayParts.year,
                      month: todayParts.month,
                      day: todayParts.day,
                    });
                  }}
                >
                  <div className="font-caption-1">
                    <FormattedMessage id="fuzzydate.today.button.label" />
                  </div>
                </TextLink>
              </div>
            ) : <div />
          }
        </>
      )}
    />
  );
};

function createMinuteOptions(value) {
  return {
    label: `00${value}`.slice(-2),
    value,
  };
}
function createOption(value) {
  return {
    label: value,
    value,
    isDisabled: false,
  };
}
const hourOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(v => createOption(v));
hourOptions.unshift({ label: '', value: null, isDisabled: true });

interface TimePickerProps {
  dateTime: iDateTime;
  onSubmit(t): void;
  onCancel(): void;
  minuteInterval?: number;
  allowNull: boolean;
  allowNow: boolean;
  style?: any;
  intl?: any;
}
export const TimePicker: React.FC<TimePickerProps> = injectIntl((props) => {
  const { onSubmit, onCancel, minuteInterval, allowNull, allowNow, style, intl } = props;
  const mInterval = minuteInterval || 1;
  const minuteOptions = _.times(60 / mInterval).map(v => createMinuteOptions(v * mInterval));
  minuteOptions.unshift({ label: '', value: null, isDisabled: true });
  const periodOptions = [
    {
      label: '',
      value: null,
      isDisabled: true,
    },
    {
      label: intl.formatMessage({ id: 'ui-data.time.am.text' }),
      value: TIME_PICKER_PERIOD.AM,
      isDisabled: false,
    },
    {
      label: intl.formatMessage({ id: 'ui-data.time.pm.text' }),
      value: TIME_PICKER_PERIOD.PM,
      isDisabled: false,
    },
  ];

  const [timeState, setTimeState] = React.useState(() => {
    const dt = new DateTimeModel(props.dateTime, intl);
    return dt.getTimeParts();
  });

  React.useEffect(() => {
    const dt = new DateTimeModel(props.dateTime, intl);
    setTimeState(dt.getTimeParts());
  }, []);

  return (
    <PickerModal
      style={style}
      phraseText={{
        label: { id: 'heReminder.time.field.label' },
        submit: { id: 'fuzzydate.submit.button.label' },
      }}
      onSubmit={() => {
        const hasTime = Number.isInteger(timeState.hours) && Number.isInteger(timeState.minutes);
        if (hasTime) {
          const timestamp = DateUtil.convert12HourTimeTo24Timestamp(timeState);
          onSubmit(timestamp);
        } else {
          onSubmit(null);
        }
      }}
      onClose={() => {
        onCancel();
      }}
      onChange={(i, field, value) => {
        const newTimeState = {
          ...timeState,
        };
        switch (i) {
          case 0:
            newTimeState.hours = Number(value);
            break;
          case 1:
            newTimeState.minutes = Number(value);
            break;
          case 2:
            newTimeState.isAM = value === TIME_PICKER_PERIOD.AM;
            break;
          default:
            break;
        }

        setTimeState(newTimeState);
      }}
      fields={[
        {
          value: timeState.hours,
          options: hourOptions,
          label: '',
        },
        {
          value: timeState.minutes,
          options: minuteOptions,
          label: '',
        },
        {
          value: timeState.isAM ? TIME_PICKER_PERIOD.AM : TIME_PICKER_PERIOD.PM,
          options: periodOptions,
          label: '',
        },
      ]}

      alternateActions={(
        <>
          {
            allowNull ? (
              <TextLink
                onClick={() => {
                  setTimeState({
                    ...timeState,
                    hours: null,
                    minutes: null,
                  });
                }}
              >
                <div className="font-caption">
                  <FormattedMessage id="fuzzydate.unsure.button.label" />
                </div>
              </TextLink>
            ) : null
          }
          {
            allowNow ? (
              <TextLink
                onClick={() => {
                  const nowTimeParts = DateUtil.breakNowTimeIntoParts(true);

                  setTimeState({
                    ...timeState,
                    hours: nowTimeParts.hours,
                    minutes: nowTimeParts.minutes,
                    isAM: nowTimeParts.period === TIME_PICKER_PERIOD.AM,
                  });
                }}
              >
                <div className="font-caption">
                  <FormattedMessage id="ui-data.time.now.text" />
                </div>
              </TextLink>
            ) : null
          }
          {
            allowNull || allowNow ? null : <div />
          }
        </>
      )}
    />
  );
});

export default DatePicker;
